// @ts-nocheck
/**
 * 市場連鎖システム - テーマ定義・連鎖関係
 *
 * chain_info/market_chain_system_complete.md に基づく実装
 *
 * 21テーマの定義と連鎖関係（正連鎖・対抗連鎖・負連鎖）、
 * 特殊連鎖（宿敵・三つ巴・生態系・覇権・技術）を定義
 */

// ========================================
// 連鎖率（減衰率）定義
// ========================================

export const CHAIN_RATES = {
  positive: 0.50,        // 正連鎖: 50%
  counter: 0.30,         // 対抗連鎖: 30%
  negative: -0.25,       // 負連鎖: -25%
  rivalry: 0.40,         // 宿敵連鎖: 40%（対抗連鎖より高い）
  dominion_vassal: 0.50, // 覇権-従属: 50%
  dominion_rival: -0.25, // 覇権-対立: -25%
  triangle_1: 0.50,      // 三つ巴-1段目: 50%
  triangle_2: 0.25,      // 三つ巴-2段目: 25%
  ecosystem: 0.50,       // 生態系連鎖（正）: 50%
  ecosystem_negative: -0.25, // 生態系連鎖（負）: -25%
  technology: 0.50,      // 技術連鎖: 50%
  attribute_ripple: 0.25, // 属性波及連鎖: 25%（カテゴリ→属性への弱い波及）
};

// ========================================
// 連鎖発生確率
// ========================================

export const CHAIN_PROBABILITIES = {
  positive: 0.70,        // 正連鎖: 70%
  counter: 0.50,         // 対抗連鎖: 50%
  negative: 0.40,        // 負連鎖: 40%
  rivalry: 0.80,         // 宿敵連鎖: 80%
  dominion: 0.70,        // 覇権連鎖: 70%
  triangle: 0.30,        // 三つ巴連鎖: 30%（稀少）
  ecosystem: 0.50,       // 生態系連鎖: 50%
  technology: 0.60,      // 技術連鎖: 60%
  attribute_ripple: 0.80, // 属性波及連鎖: 80%（高確率で発生）
};

// ========================================
// 連鎖数の制限
// ========================================

export const CHAIN_LIMITS = {
  maxTotal: 3,           // 1日あたりの連鎖効果: 最大3個
  maxPositive: 2,        // 正連鎖: 最大2個
  maxCounter: 1,         // 対抗連鎖: 最大1個
  maxNegative: 1,        // 負連鎖: 最大1個
};

// ========================================
// テーマ → カテゴリタグ・名称パターン対応表
// ========================================

export const THEME_CATEGORY_MAPPING = {
  // 炎属性系
  '岩狸': {
    attribute: '炎',
    categoryTags: ['ビースト・狸', 'マグマフォージ'],
    namePatterns: ['岩狸', '熔岩'],
  },
  'ドラゴン': {
    attribute: '炎',
    categoryTags: ['ドラゴン', 'ワイバーン'],
    namePatterns: ['ドラゴン', '竜', '龍'],
  },
  'フェニックス': {
    attributes: ['炎', '光'],
    categoryTags: ['フェニックス'],
    namePatterns: ['フェニックス', '不死鳥'],
  },

  // 水属性系
  'ブリザードキャット': {
    attribute: '水',
    categoryTags: ['ビースト・猫'],
    namePatterns: ['ブリザード', '氷猫'],
  },
  'ヴェルゼファール': {
    attribute: '水',
    categoryTags: ['ダークドウェラ', 'ディープシャドウ', 'タイドウェーブ'],
    namePatterns: ['ヴェルゼファール', '深海'],
  },
  'リヴァイアサン': {
    attribute: '水',
    categoryTags: ['リヴァイアサン'],
    namePatterns: ['リヴァイアサン', 'クラーケン'],
  },

  // 光属性系
  '鳥民': {
    attribute: '光',
    categoryTags: ['ウィング・トリビューン'],
    namePatterns: ['鳥民', 'アヴィクルス'],
  },
  'フルーツ・マリオネット': {
    attribute: '光',
    categoryTags: ['パペットマスター'],
    namePatterns: ['フルーツ・マリオネット'],
  },
  'リリカ': {
    attributes: ['光', '闇'],
    categoryTags: ['魔族'],
    namePatterns: ['リリカ', '幼魔王', 'ご主人様'],
  },

  // 闇属性系
  '魔女': {
    attribute: '闇',
    categoryTags: ['ソウルカース'],
    namePatterns: ['魔女', 'エリザヴェット'],
  },
  'ゴシック': {
    attributes: ['闇', '原始'],
    categoryTags: ['パペットマスター'],
    namePatterns: ['ゴシック'],
  },
  'シャドウ': {
    attribute: '闇',
    categoryTags: ['シャドウ', 'ダークドウェラ'],
    namePatterns: ['シャドウ'],
  },
  '黒呪': {
    attribute: '闇',
    categoryTags: ['ソウルカース'],
    namePatterns: ['黒呪', '呪骸'],
  },

  // 原始属性系
  '粘液獣': {
    attribute: '原始',
    categoryTags: ['スライム'],
    namePatterns: ['粘液獣', '粘液'],
  },
  '鎖縛': {
    attribute: '原始',
    categoryTags: ['鎖'],
    namePatterns: ['鎖縛', 'リアノン'],
  },
  '虹羽密林': {
    attributes: ['原始', '光'],
    categoryTags: ['プラント', 'インセクト'],
    namePatterns: ['虹羽密林', '虹羽'],
  },

  // 未来属性系
  'ゴーレム': {
    attribute: '未来',
    categoryTags: ['ゴーレム', 'マシン', 'エクラシア'],
    namePatterns: ['ゴーレム', '魔導', 'エクラシア'],
  },
  '未来鴉': {
    attribute: '未来',
    categoryTags: ['鳥・カラス'],
    namePatterns: ['未来鴉', 'ミラン', '紫鴉'],
  },

  // 複合/無属性系
  'ヴォランティス': {
    attributes: ['光', '炎'],
    categoryTags: ['スカイレジェンド'],
    namePatterns: ['ヴォランティス'],
  },
  '呪術狩り': {
    attributes: ['光', '闇', 'なし'],
    categoryTags: ['ライバル'],
    namePatterns: ['呪術狩り'],
  },
  '鉄槍騎士団': {
    attribute: 'なし',
    categoryTags: ['蛮族'],
    namePatterns: ['鉄槍'],
  },
};

// テーマ一覧
export const THEMES = Object.keys(THEME_CATEGORY_MAPPING);

// ========================================
// 基本連鎖関係（正連鎖・対抗連鎖・負連鎖）
// ========================================

export const CHAIN_RELATIONS = {
  // 炎属性系
  '岩狸': {
    positive: ['ドラゴン', 'ゴーレム'],
    counter: ['ブリザードキャット'],
    negative: ['鳥民'],
  },
  'ドラゴン': {
    positive: ['フェニックス', '岩狸'],
    counter: ['呪術狩り', '鉄槍騎士団'],
    negative: ['ヴェルゼファール'],
  },
  'フェニックス': {
    positive: ['ドラゴン', '鳥民'],
    counter: ['黒呪'],
    negative: ['虹羽密林'],
  },

  // 水属性系
  'ブリザードキャット': {
    positive: ['ヴェルゼファール'],
    counter: ['岩狸'],
    negative: ['粘液獣'],
  },
  'ヴェルゼファール': {
    positive: ['リヴァイアサン', 'ブリザードキャット'],
    counter: [],
    negative: ['ヴォランティス', '鳥民'],
  },
  'リヴァイアサン': {
    positive: ['ヴェルゼファール'],
    counter: [],
    negative: ['鳥民'],
  },

  // 光属性系
  '鳥民': {
    positive: ['ヴォランティス', 'フェニックス'],
    counter: ['シャドウ', '魔女'],
    negative: ['ヴェルゼファール'],
  },
  'フルーツ・マリオネット': {
    positive: ['虹羽密林'],
    counter: [],
    negative: ['ゴシック'],
  },
  'リリカ': {
    positive: ['ゴシック', '魔女'],
    counter: ['鉄槍騎士団'],
    negative: ['鳥民'],
  },

  // 闇属性系
  '魔女': {
    positive: ['黒呪', 'シャドウ', 'ゴシック'],
    counter: ['呪術狩り'],
    negative: ['フルーツ・マリオネット', '鳥民'],
  },
  'ゴシック': {
    positive: ['鎖縛', '魔女'],
    counter: [],
    negative: ['フルーツ・マリオネット'],
  },
  'シャドウ': {
    positive: ['魔女', '黒呪'],
    counter: [],
    negative: ['鳥民', 'フェニックス'],
  },
  '黒呪': {
    positive: ['魔女', 'シャドウ'],
    counter: ['フェニックス'],
    negative: ['呪術狩り'],
  },

  // 原始属性系
  '粘液獣': {
    positive: ['虹羽密林'],
    counter: [],
    negative: ['ブリザードキャット'],
  },
  '鎖縛': {
    positive: ['ゴシック', 'ゴーレム'],
    counter: [],
    negative: [],
  },
  '虹羽密林': {
    positive: ['粘液獣', 'フルーツ・マリオネット', '鳥民'],
    counter: ['ゴーレム'],
    negative: ['フェニックス'],
  },

  // 未来属性系
  'ゴーレム': {
    positive: ['未来鴉', '岩狸', '鎖縛'],
    counter: ['粘液獣'],
    negative: ['魔女', '黒呪'],
  },
  '未来鴉': {
    positive: ['ゴーレム'],
    counter: ['呪術狩り', '魔女'],
    negative: [],
  },

  // 複合/無属性系
  'ヴォランティス': {
    positive: ['鳥民', 'フェニックス'],
    counter: [],
    negative: ['ヴェルゼファール', 'リヴァイアサン'],
  },
  '呪術狩り': {
    positive: [],
    counter: ['魔女', 'リリカ', '黒呪'],
    negative: [],
  },
  '鉄槍騎士団': {
    positive: [],
    counter: ['ドラゴン', 'リリカ'],
    negative: [],
  },
};

// ========================================
// 特殊連鎖定義
// ========================================

/**
 * 宿敵連鎖（5組）
 * 対立する両者が「戦いの需要」で同時に上昇
 */
export const RIVALRY_PAIRS = [
  { themeA: '魔女', themeB: '呪術狩り', relation: '狩る者と狩られる者' },
  { themeA: 'リリカ', themeB: '鉄槍騎士団', relation: '魔王と正義の騎士' },
  { themeA: 'ドラゴン', themeB: '鉄槍騎士団', relation: '龍と竜殺しの騎士' },
  { themeA: '黒呪', themeB: 'フェニックス', relation: '呪いと不死' },
  { themeA: 'ヴェルゼファール', themeB: 'ヴォランティス', relation: '海の王と空の王' },
];

/**
 * 三つ巴連鎖（5パターン）
 * 3勢力が連鎖的に動く
 */
export const TRIANGLE_CHAINS = [
  { themes: ['ドラゴン', '呪術狩り', '魔女'], flow: '龍退治→魔女討伐→龍の報復' },
  { themes: ['粘液獣', 'ブリザードキャット', '岩狸'], flow: '増殖→凍結→焼却' },
  { themes: ['鳥民', 'シャドウ', '魔女'], flow: '浄化→潜伏→呪詛' },
  { themes: ['ゴーレム', '虹羽密林', '粘液獣'], flow: '開発→自然破壊→増殖' },
  { themes: ['未来鴉', '呪術狩り', '黒呪'], flow: '予言→討伐→呪い' },
];

/**
 * 生態系連鎖（5パターン）
 * 自然界の食物連鎖や生態系のバランスで波及
 */
export const ECOSYSTEM_CHAINS = [
  { source: '粘液獣', target: '虹羽密林', direction: 'positive', relation: '共生/競合' },
  { source: '虹羽密林', target: 'フルーツ・マリオネット', direction: 'positive', relation: '植物連動' },
  { source: '虹羽密林', target: '鳥民', direction: 'positive', relation: '生息地' },
  { source: 'ブリザードキャット', target: '粘液獣', direction: 'negative', relation: '凍結で抑制' },
  { source: 'フェニックス', target: '虹羽密林', direction: 'negative', relation: '炎で焼く' },
];

/**
 * 覇権連鎖（4領域）
 * 領域（海/空/闇/光）の覇権争いで波及
 */
export const DOMINION_CHAINS = [
  {
    ruler: 'ヴォランティス',
    domain: '空',
    vassals: ['鳥民', 'フェニックス'],
    rivals: ['ヴェルゼファール', 'リヴァイアサン'],
  },
  {
    ruler: 'ヴェルゼファール',
    domain: '海',
    vassals: ['リヴァイアサン', 'ブリザードキャット'],
    rivals: ['ヴォランティス', '鳥民'],
  },
  {
    ruler: '魔女',
    domain: '闇',
    vassals: ['黒呪', 'シャドウ', 'ゴシック', 'リリカ'],
    rivals: ['鳥民', 'フェニックス', '呪術狩り'],
  },
  {
    ruler: '鳥民',
    domain: '光',
    vassals: ['フルーツ・マリオネット', 'フェニックス'],
    rivals: ['魔女', 'シャドウ', '黒呪'],
  },
];

/**
 * 技術連鎖（4パターン）
 * 技術的な関連性で波及（鍛冶/機械/魔導）
 */
export const TECHNOLOGY_CHAINS = [
  { source: '岩狸', target: 'ゴーレム', relation: '素材供給' },
  { source: 'ゴーレム', target: '未来鴉', relation: '技術応用' },
  { source: 'ゴーレム', target: '鎖縛', relation: '拘束具技術' },
  { source: '未来鴉', targets: ['呪術狩り', '魔女'], relation: '情報提供（両方に対抗連鎖）' },
];

// ========================================
// 連鎖発動の優先順位
// ========================================

export const CHAIN_PRIORITY = [
  'rivalry',    // 1. 宿敵連鎖 - 世界観的に最も重要な対立
  'dominion',   // 2. 覇権連鎖 - 領域の覇権は大きな影響
  'triangle',   // 3. 三つ巴連鎖 - 複数勢力の連鎖は稀少
  'technology', // 4. 技術連鎖 - 技術の流れは安定的
  'ecosystem',  // 5. 生態系連鎖 - 自然の流れは緩やか
  'attribute_ripple', // 6. 属性波及連鎖 - カテゴリ→属性への波及
  'basic',      // 7. 基本連鎖 - 上記に該当しない場合
];

// ========================================
// ヘルパー関数
// ========================================

/**
 * テーマからカテゴリタグを取得
 * @param {string} theme - テーマ名
 * @returns {string[]} カテゴリタグの配列
 */
export const getCategoryTagsForTheme = (theme) => {
  return THEME_CATEGORY_MAPPING[theme]?.categoryTags || [];
};

/**
 * テーマから名称パターンを取得
 * @param {string} theme - テーマ名
 * @returns {string[]} 名称パターンの配列
 */
export const getNamePatternsForTheme = (theme) => {
  return THEME_CATEGORY_MAPPING[theme]?.namePatterns || [];
};

/**
 * テーマから関連属性を取得
 * @param {string} theme - テーマ名
 * @returns {string|string[]|null} 属性（複数の場合は配列）
 */
export const getAttributeForTheme = (theme) => {
  const mapping = THEME_CATEGORY_MAPPING[theme];
  if (!mapping) return null;
  return mapping.attributes || mapping.attribute || null;
};

/**
 * カテゴリタグからテーマを逆引き
 * @param {string} categoryTag - カテゴリタグ
 * @returns {string|null} テーマ名
 */
export const getThemeFromCategoryTag = (categoryTag) => {
  for (const [theme, mapping] of Object.entries(THEME_CATEGORY_MAPPING)) {
    if (mapping.categoryTags && mapping.categoryTags.includes(categoryTag)) {
      return theme;
    }
  }
  return null;
};

/**
 * 名称パターンからテーマを逆引き
 * @param {string} namePattern - 名称パターン
 * @returns {string|null} テーマ名
 */
export const getThemeFromNamePattern = (namePattern) => {
  for (const [theme, mapping] of Object.entries(THEME_CATEGORY_MAPPING)) {
    if (mapping.namePatterns && mapping.namePatterns.some(p => namePattern.includes(p))) {
      return theme;
    }
  }
  return null;
};

/**
 * テーマが宿敵ペアに含まれるかチェック
 * @param {string} theme - テーマ名
 * @returns {Object|null} 該当する宿敵ペア
 */
export const getRivalryPairForTheme = (theme) => {
  return RIVALRY_PAIRS.find(pair => pair.themeA === theme || pair.themeB === theme) || null;
};

/**
 * テーマが三つ巴連鎖の起点かチェック
 * @param {string} theme - テーマ名
 * @returns {Object|null} 該当する三つ巴パターン
 */
export const getTriangleChainForTheme = (theme) => {
  return TRIANGLE_CHAINS.find(chain => chain.themes[0] === theme) || null;
};

/**
 * テーマが覇権連鎖の支配者かチェック
 * @param {string} theme - テーマ名
 * @returns {Object|null} 該当する覇権定義
 */
export const getDominionForTheme = (theme) => {
  return DOMINION_CHAINS.find(dom => dom.ruler === theme) || null;
};

/**
 * テーマが技術連鎖の起点かチェック
 * @param {string} theme - テーマ名
 * @returns {Object|null} 該当する技術連鎖
 */
export const getTechnologyChainForTheme = (theme) => {
  return TECHNOLOGY_CHAINS.find(chain => chain.source === theme) || null;
};

/**
 * テーマが生態系連鎖の起点かチェック
 * @param {string} theme - テーマ名
 * @returns {Object|null} 該当する生態系連鎖
 */
export const getEcosystemChainForTheme = (theme) => {
  return ECOSYSTEM_CHAINS.find(chain => chain.source === theme) || null;
};

// ========================================
// エクスポート
// ========================================

export default {
  // 定数
  CHAIN_RATES,
  CHAIN_PROBABILITIES,
  CHAIN_LIMITS,
  CHAIN_PRIORITY,

  // テーマ定義
  THEME_CATEGORY_MAPPING,
  THEMES,

  // 基本連鎖
  CHAIN_RELATIONS,

  // 特殊連鎖
  RIVALRY_PAIRS,
  TRIANGLE_CHAINS,
  ECOSYSTEM_CHAINS,
  DOMINION_CHAINS,
  TECHNOLOGY_CHAINS,

  // ヘルパー関数
  getCategoryTagsForTheme,
  getNamePatternsForTheme,
  getAttributeForTheme,
  getThemeFromCategoryTag,
  getThemeFromNamePattern,
  getRivalryPairForTheme,
  getTriangleChainForTheme,
  getDominionForTheme,
  getTechnologyChainForTheme,
  getEcosystemChainForTheme,
};
